import type { PlotlyGraph } from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import type {
    OptimTurpeChart,
    OptimTurpeChartSubchart,
} from "@modules/courtage/types/optim-turpe"
import {
    DEFAULT_HOVER_Y_FORMAT,
    HOMEYS_PLOTLY_AXIS_TICK_FONT,
    applyHomeysStandardTraceHover,
    buildHomeysStandardPlotlyLayout,
    buildTooltipChartTemplate,
    ensureTraceDapiHover,
    extractTooltipUnitFromAxisLabel,
    finalizeHomeysPlotlyGraph,
} from "@modules/data/utils/plotly"

// ----- Profil hebdo (axe X) : aligné legacy `ChartMixedLinear` + `getTicks` (chartLinearHelpers.js) -----

/**
 * Lundi 1ᵉʳ janvier 2024 00:00 UTC — origine pour convertir l’index 0…n en timeline semaine.
 */
const PROFILE_WEEK_ORIGIN_UTC_MS = Date.UTC(2024, 0, 1, 0, 0, 0, 0)

const PROFILE_AXIS_DAY_LABELS_FR = [
    "lundi",
    "mardi",
    "mercredi",
    "jeudi",
    "vendredi",
    "samedi",
    "dimanche",
] as const

const PROFILE_TICKS = {
    ten_minutes: {
        tickIndex: [72, 216, 360, 504, 648, 792, 936] as const,
        msPerX: 10 * 60 * 1000,
    },
    hourly: {
        tickIndex: [12, 36, 60, 84, 108, 132, 156] as const,
        msPerX: 60 * 60 * 1000,
    },
} as const

type WeeklyProfileResolution = "ten_minutes" | "hourly"

function chartIsWeeklyProfile(chart: OptimTurpeChart): boolean {
    return Boolean(chart.name?.toLowerCase().includes("profil"))
}

function inferMaxXFromProfileSubcharts(chart: OptimTurpeChart): number {
    let max = 0
    for (const sc of normalizeSubcharts(chart)) {
        const pts = (sc as { content?: { points?: Array<{ x?: unknown }> } })
            .content?.points
        if (!Array.isArray(pts)) continue
        for (const p of pts) {
            const xv = (p as { x?: unknown }).x
            if (typeof xv === "number" && Number.isFinite(xv) && xv > max) {
                max = xv
            }
        }
    }
    return max
}

/**
 * Déduit 10 min vs horaire à partir du max d’index `x` (legacy `WEEKLY_PERIOD` : 1007 vs 167).
 */
function inferWeeklyProfileResolution(
    maxX: number
): WeeklyProfileResolution | null {
    if (maxX < 100) return null
    if (maxX >= 400) return "ten_minutes"
    return "hourly"
}

function profileIndexToIsoUtc(
    x: number,
    resolution: WeeklyProfileResolution
): string {
    const msPerX =
        resolution === "ten_minutes"
            ? PROFILE_TICKS.ten_minutes.msPerX
            : PROFILE_TICKS.hourly.msPerX
    return new Date(PROFILE_WEEK_ORIGIN_UTC_MS + x * msPerX).toISOString()
}

function mapNumericXToProfileTimeline(
    xValues: Array<string | number>,
    resolution: WeeklyProfileResolution
): Array<string | number> {
    return xValues.map((v) =>
        typeof v === "number" && Number.isFinite(v)
            ? profileIndexToIsoUtc(v, resolution)
            : v
    )
}

function buildWeeklyProfileXAxisLayout(
    resolution: WeeklyProfileResolution
): Record<string, unknown> {
    const cfg =
        resolution === "ten_minutes"
            ? PROFILE_TICKS.ten_minutes
            : PROFILE_TICKS.hourly
    const tickvals = cfg.tickIndex.map((idx) =>
        new Date(PROFILE_WEEK_ORIGIN_UTC_MS + idx * cfg.msPerX).toISOString()
    )
    return {
        type: "date",
        tickmode: "array",
        tickvals,
        ticktext: [...PROFILE_AXIS_DAY_LABELS_FR],
        /** Jour + heure au survol (équiv. `findDay` + hover legacy). */
        hoverformat: "%A %d %b %H:%M",
        tickfont: { ...HOMEYS_PLOTLY_AXIS_TICK_FONT },
    }
}

function finalizePlotlyGraph(
    graph: PlotlyGraph,
    chart: OptimTurpeChart
): PlotlyGraph {
    return finalizeHomeysPlotlyGraph(graph, chart)
}

/** Aligné sur homeys-front : `chart.${subchart.name}` pour les légendes Plotly. */
export type BuildOptimTurpePlotlyGraphOptions = {
    legendLabel?: (apiIdentifier: string | undefined) => string
}

function toPlotlyLineDash(type?: string): "solid" | "dash" {
    if (type === "dashed_line") {
        return "dash"
    }
    return "solid"
}

function parseJsonIfString<T>(raw: unknown): T | undefined {
    if (raw == null) return undefined
    if (typeof raw !== "string") return raw as T
    try {
        return JSON.parse(raw) as T
    } catch {
        return undefined
    }
}

/**
 * Réponse API parfois : `data` / `layout` en strings JSON, ou `{ data, layout }` dans une seule string.
 */
function tryPlotlyDataLayout(chart: OptimTurpeChart): PlotlyGraph | null {
    let dataRaw = chart.data as unknown
    let layoutRaw = chart.layout as unknown

    dataRaw = parseJsonIfString(dataRaw) ?? dataRaw
    layoutRaw = parseJsonIfString(layoutRaw) ?? layoutRaw

    if (
        dataRaw &&
        typeof dataRaw === "object" &&
        !Array.isArray(dataRaw) &&
        Array.isArray((dataRaw as { data?: unknown }).data)
    ) {
        const bundle = dataRaw as { data: object[]; layout?: object }
        return finalizePlotlyGraph(
            {
                data: bundle.data,
                layout: (bundle.layout ?? {}) as Record<string, unknown>,
            },
            chart
        )
    }

    if (Array.isArray(dataRaw) && dataRaw.length > 0) {
        const layout =
            layoutRaw &&
            typeof layoutRaw === "object" &&
            !Array.isArray(layoutRaw)
                ? (layoutRaw as object)
                : {}
        return finalizePlotlyGraph(
            {
                data: dataRaw as object[],
                layout: layout as Record<string, unknown>,
            },
            chart
        )
    }

    return null
}

function normalizeSubcharts(chart: OptimTurpeChart): OptimTurpeChartSubchart[] {
    const subs = chart.subcharts as
        | Record<string, OptimTurpeChartSubchart>
        | OptimTurpeChartSubchart[]
        | undefined
    if (!subs) return []
    if (Array.isArray(subs)) return subs
    return Object.values(subs)
}

function coerceAxis(raw: unknown): Array<string | number> | null {
    if (!Array.isArray(raw) || raw.length === 0) return null
    return raw as Array<string | number>
}

/**
 * Format API métier : `content.points` = liste de `{ x, y }` (dates ISO + nombre).
 */
function axesFromContentPoints(content: unknown): {
    x: Array<string | number>
    y: number[]
} | null {
    if (!content || typeof content !== "object") return null
    const pts = (content as { points?: unknown }).points
    if (!Array.isArray(pts) || pts.length === 0) return null

    const x: Array<string | number> = []
    const y: number[] = []

    for (const p of pts) {
        if (!p || typeof p !== "object") continue
        const px = (p as { x?: unknown }).x
        const py = (p as { y?: unknown }).y
        if (py === undefined || py === null) continue

        const xn =
            typeof px === "number"
                ? Number.isFinite(px)
                    ? px
                    : null
                : typeof px === "string"
                  ? px.trim() !== ""
                      ? px
                      : null
                  : null
        if (xn === null) continue

        const yn = typeof py === "number" ? py : Number(py)
        if (!Number.isFinite(yn)) continue
        x.push(xn)
        y.push(yn)
    }

    if (x.length === 0 || y.length === 0 || x.length !== y.length) return null
    return { x, y }
}

function resolveLegendName(
    apiName: string | undefined,
    legendLabel?: BuildOptimTurpePlotlyGraphOptions["legendLabel"]
): string | undefined {
    if (!legendLabel) return apiName
    return legendLabel(apiName)
}

type MapSubchartContext = {
    yTooltipLabel: string
    weeklyProfileResolution: WeeklyProfileResolution | null
}

function mapSubchartToTrace(
    subchart: OptimTurpeChartSubchart,
    options: BuildOptimTurpePlotlyGraphOptions | undefined,
    ctx: MapSubchartContext
): object | null {
    const raw = subchart as Record<string, unknown>
    const legendLabel = options?.legendLabel
    const { yTooltipLabel, weeklyProfileResolution } = ctx

    const fromPoints = axesFromContentPoints(subchart.content as unknown)

    let xRaw = raw.x ?? subchart.content?.x ?? fromPoints?.x
    let yRaw =
        raw.y ??
        subchart.content?.y ??
        (fromPoints?.y as unknown as Array<string | number>)

    xRaw = parseJsonIfString<Array<string | number>>(xRaw) ?? xRaw
    yRaw = parseJsonIfString<Array<string | number>>(yRaw) ?? yRaw

    /**
     * Traces déjà au format Plotly (pas les alias métier `line` / `dashed_line`,
     * convertis plus bas en `scatter` + `line.dash`).
     */
    const typeStr = typeof raw.type === "string" ? raw.type : ""
    const isTemplateLineAlias =
        typeStr === "line" || typeStr === "dashed_line" || typeStr === "area"
    if (
        typeStr !== "" &&
        !isTemplateLineAlias &&
        Array.isArray(xRaw) &&
        Array.isArray(yRaw)
    ) {
        const passthroughName =
            typeof raw.name === "string" ? raw.name : subchart.name
        const displayName =
            resolveLegendName(passthroughName, legendLabel) ?? ""
        let xOut: unknown = xRaw
        if (
            weeklyProfileResolution &&
            Array.isArray(xRaw) &&
            (xRaw as unknown[]).every((v) => typeof v === "number")
        ) {
            xOut = mapNumericXToProfileTimeline(
                xRaw as number[],
                weeklyProfileResolution
            )
        }
        const base = {
            ...(raw as object),
            x: xOut,
            y: yRaw,
            name: displayName,
        }
        return ensureTraceDapiHover(
            base as Record<string, unknown>,
            yTooltipLabel
        )
    }

    let xa = coerceAxis(xRaw)
    const ya = coerceAxis(yRaw)

    /** Ordonnées seules : abscisse = index (profils / historiques indexés). */
    if (!xa && ya && ya.length > 0) {
        xa = ya.map((_, i) => i)
    }

    if (!xa || !ya) return null

    const len = Math.min(xa.length, ya.length)
    if (len === 0) return null

    let xTrim: Array<string | number> = xa.slice(0, len)
    const yTrim = ya.slice(0, len)

    if (weeklyProfileResolution && xTrim.every((v) => typeof v === "number")) {
        xTrim = mapNumericXToProfileTimeline(xTrim, weeklyProfileResolution)
    }

    const color = subchart.content?.colors?.[0] ?? "#2a4483"
    const type = subchart.type === "area" ? "scatter" : "scatter"
    const mode = subchart.type === "area" ? "lines" : "lines"

    const displayName = resolveLegendName(subchart.name, legendLabel) ?? ""

    return {
        x: xTrim,
        y: yTrim,
        type,
        mode,
        name: displayName,
        meta: [displayName],
        hovertemplate: buildTooltipChartTemplate({
            yFormat: DEFAULT_HOVER_Y_FORMAT,
            unit: yTooltipLabel,
            hasCustomData: false,
        }),
        fill: subchart.type === "area" ? "tozeroy" : undefined,
        line: {
            color,
            dash: toPlotlyLineDash(subchart.type),
        },
    }
}

export function buildOptimTurpePlotlyGraph(
    chart: OptimTurpeChart | undefined,
    options?: BuildOptimTurpePlotlyGraphOptions
): PlotlyGraph | null {
    if (!chart) return null

    const fromDataLayout = tryPlotlyDataLayout(chart)
    if (fromDataLayout) return fromDataLayout

    const yTooltip = extractTooltipUnitFromAxisLabel(
        chart.y_label?.trim() ?? ""
    )

    let weeklyProfileResolution: WeeklyProfileResolution | null = null
    if (chartIsWeeklyProfile(chart)) {
        const maxX = inferMaxXFromProfileSubcharts(chart)
        weeklyProfileResolution = inferWeeklyProfileResolution(maxX)
    }

    const mapCtx: MapSubchartContext = {
        yTooltipLabel: yTooltip,
        weeklyProfileResolution,
    }

    const subcharts = normalizeSubcharts(chart)
    const traces = subcharts
        .map((subchart) => mapSubchartToTrace(subchart, options, mapCtx))
        .filter((trace): trace is object => trace !== null)

    if (traces.length === 0) {
        return null
    }

    const tracesOut = applyHomeysStandardTraceHover(traces, {
        chart,
        yAxisLabel: chart.y_label ?? "",
    })

    const xaxisLayout: Record<string, unknown> = {
        ...(weeklyProfileResolution
            ? buildWeeklyProfileXAxisLayout(weeklyProfileResolution)
            : {}),
    }

    return {
        data: tracesOut,
        layout: buildHomeysStandardPlotlyLayout(
            {
                margin: { l: 68, r: 28, t: 40, b: 40 },
                xaxis: xaxisLayout,
                yaxis: {},
                showlegend: true,
            },
            chart
        ),
    }
}
